import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, ListGroup, InputGroup } from 'react-bootstrap';
import { 
  FaRobot, 
  FaUser, 
  FaPaperPlane, 
  FaArrowLeft, 
  FaTasks, 
  FaPlay, 
  FaClock, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaHistory, 
  FaPlus, 
  FaComments, 
  FaTrash, 
  FaEdit, 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaEyeSlash, 
  FaCog, 
  FaChevronLeft, 
  FaChevronRight, 
  FaSync, 
  FaExpand, 
  FaCompress, 
  FaTimes,
  FaWifi,
  FaExclamationCircle,
  FaMicrophone
} from 'react-icons/fa';
import Spinner from '../components/Spinner';
import ConversationSettingsModal from '../components/ConversationSettingsModal';
import VoiceRecordButton from '../components/VoiceRecordButton';
import { useAuth } from '../context/AuthContext';
import * as conversationApi from '../services/conversationApi';
import websocketService from '../services/websocketService';
import ConversationStorageService from '../services/conversationStorageService';
import { transcribeAudioSmart } from '../services/transcriptionApi';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import agentLogo from '../assets/IMG_0118.png';
import './AgentChat.css';


// Custom Modal Component
const CustomModal = ({ show, onHide, title, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('custom-modal-open');
    } else {
      document.body.style.overflow = '';
      document.body.classList.remove('custom-modal-open');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('custom-modal-open');
    };
  }, [show]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onHide();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onHide();
  };

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="custom-modal-overlay" onClick={handleBackdropClick}>
      <div className={`custom-modal-container custom-modal-${size}`}>
        <div className="custom-modal-header">
          <h4 className="custom-modal-title">{title}</h4>
          <button className="custom-modal-close" onClick={onHide}><FaTimes /></button>
        </div>
        <div className="custom-modal-body">{children}</div>
        {footer && <div className="custom-modal-footer">{footer}</div>}
      </div>
    </div>
  );
};

const AgentChat = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const MAIN_AGENT = {
    id: 'Agent Developer',
    name: 'Agent Developer Assistant',
    description: 'Main AI agent for creating and managing other agents'
  };
  
  // ── Core state ──────────────────────────────────────────────────────────────────────────────────
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [serverError, setServerError] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [currentConversationTasks, setCurrentConversationTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');
  
  const [conversationsPanelExpanded, setConversationsPanelExpanded] = useState(true);
  const [conversationSearch, setConversationSearch] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  
  // ── WebSocket state ────────────────────────────────────────────────────────────────────────────
  const [wsConnected, setWsConnected] = useState(false);
  const [wsReconnecting, setWsReconnecting] = useState(false);
  
  // ── Modal state ────────────────────────────────────────────────────────────────────────────────
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [executingTask, setExecutingTask] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [taskExecutionResult, setTaskExecutionResult] = useState(null);

  // ── Voice transcription state ──────────────────────────────────────────────────────────────────
  const [voiceError, setVoiceError] = useState(null);
  
  // ── Refs ───────────────────────────────────────────────────────────────────────────────────────
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Ref to track current conversation ID without stale closures
  const currentConversationIdRef = useRef(currentConversationId);

  // Keep the ref in sync with state
  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // ── Voice recorder setup ───────────────────────────────────────────────────────────────────────

  /**
   * Called when transcription succeeds.
   * Appends the transcribed text to whatever is already in the input box,
   * then focuses the input so the user can review / edit before sending.
   */
  const handleTranscriptionComplete = (transcription) => {
    if (transcription?.trim()) {
      setNewMessage(prev =>
        prev.trim()
          ? `${prev.trim()} ${transcription.trim()}`
          : transcription.trim()
      );
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
    setVoiceError(null);
  };

  /**
   * Thin wrapper passed to stopAndTranscribe so the hook can call
   * our chosen transcription method.
   */
  const transcribeAudio = async (audioBlob, onProgress) => {
    return await transcribeAudioSmart(audioBlob, {
      language:       'en-US',
      environment:    'prod',
      sessionId:      currentConversationId,
      onProgress:     (pct, status) => onProgress?.(pct, status),
      shortThreshold: 500_000   // blobs under 500 KB use fast sync path
    });
  };

  const {
      isRecording,
      isPaused,
      isTranscribing,
      transcribeProgress,          
      recordingDuration,
      formattedDuration,
      audioLevel,
      error: recorderError,
      startRecording,
      stopAndTranscribe,
      pauseRecording,
      resumeRecording,
      cancelRecording,
      isSupported: isVoiceSupported
    } = useVoiceRecorder({
      onTranscriptionComplete: handleTranscriptionComplete,
      onError: (err) => setVoiceError(err),
      maxDurationMs: 120000
    });

  // ── HELPER: Standardize message objects with proper timezone handling ────────────────────────
  const formatMessage = (msg, source = 'server') => {
    let timestampObj;
    try {
      const raw = msg.timestamp || msg.createdAt || new Date();
      
      if (raw instanceof Date) {
        timestampObj = raw;
      } else if (typeof raw === 'string') {
        if (raw.endsWith('Z') || raw.includes('+') || raw.includes('-')) {
          timestampObj = new Date(raw);
        } else {
          // No timezone info — assume UTC
          timestampObj = new Date(raw + 'Z');
        }
      } else {
        timestampObj = new Date(raw);
      }
      
      if (isNaN(timestampObj.getTime())) {
        timestampObj = new Date();
      }
    } catch(e) {
      console.error('Error parsing timestamp:', e);
      timestampObj = new Date();
    }
  
    return {
      id: msg.id || msg.messageId || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: msg.content || msg.message || '',
      role: msg.role || (msg.sender === 'user' ? 'user' : 'assistant'),
      timestamp: timestampObj,
      intent: msg.intent,
      ticketId: msg.ticketId,
      executionArn: msg.executionArn,
      isPartialResponse: msg.isPartialResponse,
      isError: msg.isError,
      source: source
    };
  };

  // ── WebSocket message handler ──────────────────────────────────────────────────────────────────
  const handleWebSocketMessage = (data) => {
    console.log('🔔 WebSocket message received:', data);
  
    // 1. Robust message type detection
    let messageType = data.type || data.action || data.notificationType;
    const internalData = data.data || {};
    
    // Handle backend defaulting to 'new_message'
    if (messageType === 'new_message') {
      if (internalData.notificationType === 'session_update' || data.notificationType === 'session_update') {
        messageType = 'session_update';
      } else if (internalData.updateType === 'title_changed' || internalData.notificationType === 'conversation_update') {
        messageType = 'conversation_update';
      }
    }
    
    switch (messageType) {
      case 'session_update': {
        console.log('📝 Session update received:', data);
        const sessionData = data.data || data;
        
        if (sessionData.sessionId && sessionData.isNewSession) {
          const oldSessionId = currentConversationIdRef.current; 
          const newSessionId = sessionData.sessionId;
          
          console.log('🔄 Upgrading session:', { from: oldSessionId, to: newSessionId });
          
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.sessionId === oldSessionId || conv.sessionId === newSessionId) {
                return {
                  ...conv,
                  sessionId: newSessionId,
                  title: (sessionData.title && sessionData.title !== 'New Conversation') 
                    ? sessionData.title 
                    : conv.title,
                  isTemporary: false,
                  source: 'server',
                  lastActivity: sessionData.timestamp || new Date().toISOString()
                };
              }
              return conv;
            });
            
            if (oldSessionId && oldSessionId !== newSessionId) {
              ConversationStorageService.updateConversationSessionId(oldSessionId, newSessionId);
            }
            return updated;
          });

          setCurrentConversationId(newSessionId);
          
          if (websocketService.isConnected()) {
            websocketService.subscribe(newSessionId);
          }
        }
        break;
      }
  
      case 'conversation_update': {
        console.log('📝 Conversation update received:', data);
        const updateData = data.data || data;
        
        if (updateData.updateType === 'title_changed' && updateData.title) {
          setConversations(prev => {
            let found = false;
            const updated = prev.map(conv => {
              if (
                conv.sessionId === updateData.sessionId || 
                (currentConversationIdRef.current && conv.sessionId === currentConversationIdRef.current)
              ) {
                console.log('✅ Updating title:', conv.title, '->', updateData.title);
                found = true;
                return { 
                  ...conv, 
                  title: updateData.title,
                  lastActivity: updateData.timestamp || new Date().toISOString()
                };
              }
              return conv;
            });
            
            if (found) {
              ConversationStorageService.saveConversations(updated);
            }
            return updated;
          });
        }
        break;
      }
  
      case 'new_message':
      case 'message': {
        const messageData = data.data || data.message || data;
        
        if (messageData.content || messageData.message) {
          const newMsg = formatMessage({
            id: messageData.messageId || `ws-${Date.now()}`,
            content: messageData.content || messageData.message,
            role: messageData.role || 'assistant',
            timestamp: messageData.timestamp || new Date().toISOString(),
            intent: messageData.intent,
            ticketId: messageData.ticketId
          }, 'websocket');
          
          setMessages(prev => {
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          
          setTimeout(() => scrollToBottom("smooth"), 100);
        }
        break;
      }
  
      case 'task_update': {
        console.log('📋 Task update received:', data);
        const taskUpdateData = data.data || data.task || data;
        
        setCurrentConversationTasks(prev => {
          const existingIndex = prev.findIndex(t => t.ticketId === taskUpdateData.ticketId);
          
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = { ...updated[existingIndex], ...taskUpdateData };
            return updated;
          } else {
            const newTask = {
              ticketId: taskUpdateData.ticketId,
              title: taskUpdateData.title || taskUpdateData.message || 'New Task',
              status: taskUpdateData.status || 'CREATED',
              createdAt: taskUpdateData.timestamp || new Date().toISOString(),
              updatedAt: taskUpdateData.timestamp || new Date().toISOString(),
              description: taskUpdateData.description || taskUpdateData.message || '',
              sessionId: currentConversationId,
              executionArn: taskUpdateData.executionArn
            };
            return [newTask, ...prev];
          }
        });
        
        if (taskUpdateData.status === 'CREATED') {
          setSuccess(`Task created: ${taskUpdateData.title || 'New task'}`);
        } else if (taskUpdateData.status === 'EXECUTING') {
          setSuccess(`Task executing: ${taskUpdateData.title || 'Task'}`);
        }
        setTimeout(() => setSuccess(null), 3000);
        break;
      }
  
      case 'task_completion': {
        console.log('✅ Task completion received:', data);
        const completionData = data.data || data.task || data;
        
        setCurrentConversationTasks(prev => 
          prev.map(task => 
            task.ticketId === completionData.ticketId 
              ? { 
                  ...task, 
                  status: 'COMPLETED', 
                  updatedAt: new Date().toISOString(),
                  result: completionData.result || completionData.message
                }
              : task
          )
        );
        
        if (completionData.message) {
          const notificationMsg = formatMessage({
            id: `task-complete-${completionData.ticketId || Date.now()}`,
            content: completionData.message,
            role: 'assistant',
            timestamp: completionData.timestamp || new Date().toISOString(),
            ticketId: completionData.ticketId
          }, 'websocket');
          
          setMessages(prev => {
            if (prev.some(m => m.id === notificationMsg.id)) return prev;
            return [...prev, notificationMsg];
          });
          setTimeout(() => scrollToBottom("smooth"), 100);
        }
        
        if (currentConversationId) {
          loadTasksForConversation(currentConversationId);
        }
        
        setSuccess('Task completed!');
        setTimeout(() => setSuccess(null), 3000);
        break;
      }
  
      case 'task_error': {
        console.log('❌ Task error received:', data);
        const errorData = data.data || data.task || data;
        
        setCurrentConversationTasks(prev => 
          prev.map(task => 
            task.ticketId === errorData.ticketId 
              ? { ...task, status: 'FAILED', updatedAt: new Date().toISOString() }
              : task
          )
        );
        
        if (currentConversationId) {
          loadTasksForConversation(currentConversationId);
        }
        
        setError(`Task failed: ${errorData.message || 'Unknown error'}`);
        break;
      }
  
      case 'pong':
        break;
  
      case 'subscribed':
        console.log('✅ Subscribed to session:', data.sessionId);
        break;
  
      default:
        console.log('Unhandled WebSocket message:', messageType, data);
        
        if (data.content || (data.data && data.data.content)) {
          const content = data.content || data.data.content;
          const fallbackMsg = formatMessage({
            content: content,
            role: 'assistant',
            timestamp: data.timestamp || new Date().toISOString()
          }, 'websocket');
          
          setMessages(prev => {
            if (prev.some(m => m.id === fallbackMsg.id)) return prev;
            return [...prev, fallbackMsg];
          });
          setTimeout(() => scrollToBottom("smooth"), 100);
        }
    }
  };

  // ── WebSocket connection handler ───────────────────────────────────────────────────────────────
  const handleWebSocketConnection = (event) => {
    console.log('WebSocket connection event:', event.type);

    switch (event.type) {
      case 'connected':
        setWsConnected(true);
        setWsReconnecting(false);
        console.log('✅ WebSocket connected');
        break;

      case 'disconnected':
        setWsConnected(false);
        console.log('🔌 WebSocket disconnected');
        break;

      case 'error':
        setWsConnected(false);
        console.error('❌ WebSocket error:', event.error);
        break;

      case 'max_reconnect_attempts':
        setWsConnected(false);
        setWsReconnecting(false);
        setError('Lost connection to server. Please refresh the page.');
        break;

      default:
        console.log('Unknown connection event:', event.type);
    }
  };

  // ── Initialize WebSocket ───────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;

    const userId = currentUser.username || currentUser.email || 'anonymous';

    websocketService.connect(userId, currentConversationId);

    const removeMessageHandler = websocketService.addMessageHandler(handleWebSocketMessage);
    const removeConnectionHandler = websocketService.addConnectionHandler(handleWebSocketConnection);

    return () => {
      removeMessageHandler();
      removeConnectionHandler();
      websocketService.disconnect();
    };
  }, [currentUser]);

  // ── Update WebSocket subscription when conversation changes ──────────────────────────────────
  useEffect(() => {
    if (currentConversationId && websocketService.isConnected()) {
      websocketService.subscribe(currentConversationId);
    }
  }, [currentConversationId]);

  // ── Auto-scroll (IMPROVED) ─────────────────────────────────────────────────────────────────────
  const scrollToBottom = (behavior = "smooth") => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: behavior,
          block: "end"
        });
      }
    });
  };

  // Scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Use a slight delay to ensure DOM has fully rendered
      setTimeout(() => scrollToBottom("smooth"), 50);
    }
  }, [messages]);

  useEffect(() => {
    loadConversationHistory();
    loadUserPreferences();
  }, [currentUser]);

  useEffect(() => {
    if (currentConversationId) {
      loadConversationMessages(currentConversationId);
      loadTasksForConversation(currentConversationId);
      ConversationStorageService.setActiveConversationId(currentConversationId);
      setConversationsPanelExpanded(false);
    } else {
      setMessages([]);
      setCurrentConversationTasks([]);
      ConversationStorageService.setActiveConversationId(null);
    }
  }, [currentConversationId]);

  useEffect(() => {
    if (currentConversationId && !showExecuteModal && !showSettingsModal && !isRecording) {
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  }, [currentConversationId, showExecuteModal, showSettingsModal, isRecording]);

  const handleMessageInputFocus = () => {
    if (conversationsPanelExpanded) setConversationsPanelExpanded(false);
  };

  // ── Preferences ────────────────────────────────────────────────────────────────────────────────
  const loadUserPreferences = () => {
    const prefs = ConversationStorageService.getUserPreferences();
    setTaskFilter(prefs.taskFilter || 'all');
  };

  // ── Conversation loading ───────────────────────────────────────────────────────────────────────
  const loadConversationHistory = async () => {
    try {
      setLoadingConversations(true);
      setServerError(false);
      
      try {
        const serverResponse = await conversationApi.getUserConversations();
        
        if (serverResponse && serverResponse.success && serverResponse.conversations) {
          const serverConversations = serverResponse.conversations.map(conv => ({
            sessionId: conv.sessionId,
            title: conv.title || 'Untitled Conversation',
            createdAt: conv.createdAt,
            lastActivity: conv.updatedAt || conv.lastActivity || conv.createdAt,
            messageCount: parseInt(conv.messageCount) || 0,
            userId: conv.userId || currentUser?.username || currentUser?.email || 'anonymous',
            isActive: true,
            source: 'server'
          }));
          
          serverConversations.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
          setConversations(serverConversations);
          ConversationStorageService.saveConversations(serverConversations);
          
          const activeId = ConversationStorageService.getActiveConversationId();
          if (activeId && serverConversations.find(conv => conv.sessionId === activeId)) {
            setCurrentConversationId(activeId);
          } else if (serverConversations.length > 0) {
            setCurrentConversationId(serverConversations[0].sessionId);
          }
          return;
        } else {
          throw new Error('Server returned empty conversation list');
        }
      } catch (serverError) {
        console.error('Failed to fetch conversations from server:', serverError.message);
        setServerError(true);
        
        const cachedConversations = ConversationStorageService.getConversations();
        if (cachedConversations && cachedConversations.length > 0) {
          const markedCachedConversations = cachedConversations.map(conv => ({
            ...conv,
            source: 'cache'
          }));
          setConversations(markedCachedConversations);
          
          const activeId = ConversationStorageService.getActiveConversationId();
          if (activeId && markedCachedConversations.find(conv => conv.sessionId === activeId)) {
            setCurrentConversationId(activeId);
          } else if (markedCachedConversations.length > 0) {
            setCurrentConversationId(markedCachedConversations[0].sessionId);
          }
          
          setError('Unable to connect to server. Showing cached conversations. Some data may be outdated.');
          return;
        }
      }
      
      // Fallback: create a default local conversation
      const defaultConversation = {
        sessionId: `default-${Date.now()}`,
        title: 'Welcome Chat',
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        messageCount: 0,
        userId: currentUser?.username || currentUser?.email || 'anonymous',
        isActive: true,
        source: 'default'
      };
      
      setConversations([defaultConversation]);
      setCurrentConversationId(defaultConversation.sessionId);
      ConversationStorageService.saveConversations([defaultConversation]);
      
    } catch (err) {
      console.error('Critical error loading conversation history:', err);
      setError('Failed to load conversation history');
      setServerError(true);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (sessionId) => {
    try {
      setError(null);
      
      try {
        const response = await conversationApi.getConversationHistory(sessionId);
        
        if (response.success && response.messages) {
          // Sort messages by timestamp to ensure chronological order (oldest first)
          const sortedMessages = response.messages.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.createdAt).getTime();
            const timeB = new Date(b.timestamp || b.createdAt).getTime();
            return timeA - timeB;
          });
          
          const formattedMessages = sortedMessages.map(msg => formatMessage(msg, 'server'));
          setMessages(formattedMessages);
          ConversationStorageService.cacheConversationMessages(sessionId, formattedMessages);
          
          // Force scroll to bottom after messages are set
          setTimeout(() => {
            scrollToBottom("auto");
          }, 100);
          
          updateConversationActivity(sessionId, formattedMessages.length);
          return;
        } 
      } catch (apiError) {
        const cachedMessages = ConversationStorageService.getCachedConversationMessages(sessionId);
        if (cachedMessages && cachedMessages.length > 0) {
          // Sort cached messages as well
          const sortedCachedMessages = cachedMessages.sort((a, b) => {
            const timeA = new Date(a.timestamp || a.createdAt).getTime();
            const timeB = new Date(b.timestamp || b.createdAt).getTime();
            return timeA - timeB;
          });
          
          const markedCachedMessages = sortedCachedMessages.map(msg => formatMessage(msg, 'cache'));
          setMessages(markedCachedMessages);
          
          // Force scroll for cached messages
          setTimeout(() => {
            scrollToBottom("auto");
          }, 100);
          
          if (!serverError) setError('Unable to load latest messages from server. Showing cached messages.');
          return;
        }
      }
      
      setMessages([]);
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    }
  };

  const updateConversationActivity = (sessionId, messageCount = null) => {
    if (!serverError) {
      const updates = {};
      if (messageCount !== null) updates.messageCount = messageCount;
      
      setConversations(prev => prev.map(conv => 
        conv.sessionId === sessionId 
          ? { ...conv, ...updates, lastActivity: new Date().toISOString() }
          : conv
      ));
      
      ConversationStorageService.updateConversationActivity(sessionId, updates);
    }
  };

  const loadTasksForConversation = async (sessionId) => {
    if (!sessionId) {
      setCurrentConversationTasks([]);
      return;
    }
    
    try {
      setLoadingTasks(true);
      const response = await conversationApi.getUserTasks(sessionId);
      
      if (response.success && response.tasks) {
        const formattedTasks = response.tasks.map(task => ({
          ticketId: task.ticketId,
          title: task.title || task.details?.description || 'Untitled Task',
          status: task.status || 'CREATED',
          createdAt: task.createdAt,
          updatedAt: task.updatedAt,
          description: task.description || task.details?.description || 'No description available',
          details: task.details || {},
          sessionId: task.sessionId,
          executionArn: task.executionArn,
          priority: task.priority || task.details?.priority || 'medium'
        }));
        
        setCurrentConversationTasks(formattedTasks);
      } else {
        setCurrentConversationTasks([]);
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      setCurrentConversationTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // ── Conversation management ────────────────────────────────────────────────────────────────────
  const toggleConversationsPanel = () => setConversationsPanelExpanded(!conversationsPanelExpanded);

  const createNewConversation = () => {
    const userId = currentUser?.username || currentUser?.email || 'anonymous';
    const tempSessionId = `temp-${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newConversation = {
      sessionId: tempSessionId,
      title: 'New Conversation',
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      messageCount: 0,
      userId: userId,
      isActive: true,
      isTemporary: true,
      source: 'local'
    };
    
    setConversations([newConversation, ...conversations]);
    if (!serverError) ConversationStorageService.saveConversations([newConversation, ...conversations]);
    
    setCurrentConversationId(tempSessionId);
    setMessages([]);
    setCurrentConversationTasks([]);
    setError(null);
    setSuccess(null);
    setConversationsPanelExpanded(false);
    
    setTimeout(() => messageInputRef.current?.focus(), 100);
  };

  const deleteConversation = (sessionId, e) => {
    e.stopPropagation();
    
    const conversation = conversations.find(conv => conv.sessionId === sessionId);
    if (conversation?.source === 'server') {
      alert('Server conversations cannot be deleted from this interface. Please use the server admin tools.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      const updatedConversations = conversations.filter(conv => conv.sessionId !== sessionId);
      setConversations(updatedConversations);
      ConversationStorageService.deleteConversation(sessionId);
      
      if (currentConversationId === sessionId) {
        setCurrentConversationId(updatedConversations.length > 0 ? updatedConversations[0].sessionId : null);
      }
    }
  };

  const handleConversationSelect = (sessionId) => {
    setError(null);
    setSuccess(null);
    setCurrentConversationId(sessionId);
  };

  // ── Send message ───────────────────────────────────────────────────────────────────────────────
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || loading || isProcessing || !currentConversationId) return;
    
    const userMessage = formatMessage({
      content: newMessage.trim(),
      role: 'user',
      sender: 'user',
      timestamp: new Date()
    }, 'local');
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setError(null);
    setSuccess(null);
    setLoading(true);
    setIsProcessing(true);
    setProcessingMessage('Sending message...');
    
    if (conversationsPanelExpanded) setConversationsPanelExpanded(false);
    setTimeout(() => scrollToBottom("auto"), 50);
    
    let sessionIdForRequest = currentConversationId;
    
    try {
      setProcessingMessage('Processing your request...');
      
      const response = await conversationApi.sendMessage(
        userMessage.content, 
        sessionIdForRequest.startsWith('temp-') ? null : sessionIdForRequest, 
        currentUser?.username || currentUser?.email || 'anonymous'
      );
      
      if (response.success) {
        let finalSessionId = sessionIdForRequest;
        
        if (response.sessionId && response.sessionId !== sessionIdForRequest) {
          finalSessionId = response.sessionId;
          
          console.log(`📝 Session ID changed: ${sessionIdForRequest} -> ${finalSessionId}`);

          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.sessionId === sessionIdForRequest || conv.sessionId === finalSessionId) {
                const newTitle = (response.title && response.title !== 'New Conversation') 
                  ? response.title 
                  : conv.title;

                return { 
                  ...conv, 
                  sessionId: finalSessionId, 
                  title: newTitle,
                  isTemporary: false, 
                  lastActivity: new Date().toISOString(), 
                  source: 'server' 
                };
              }
              return conv;
            });

            ConversationStorageService.saveConversations(updated);
            return updated;
          });
          
          setCurrentConversationId(finalSessionId);
          
          if (sessionIdForRequest.startsWith('temp-')) {
            ConversationStorageService.clearConversationMessages(sessionIdForRequest);
          }
          
          if (websocketService.isConnected()) {
            websocketService.subscribe(finalSessionId);
          }
          
          ConversationStorageService.setActiveConversationId(finalSessionId);
        }
  
        updateConversationActivity(finalSessionId, messages.length + 1);
        
        if (response.ticketId) {
          setSuccess(`Task created successfully! Ticket ID: ${response.ticketId}`);
        }
      } else {
        throw new Error(response.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setError(`Failed to send message: ${err.message}`);
      
      setMessages(prev => [...prev, formatMessage({
        content: 'I apologize, but I encountered an error processing your message. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
        isError: true
      }, 'error')]);
      
      setTimeout(() => scrollToBottom("auto"), 100);
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setProcessingMessage('');
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  };

  // ── Task execution ─────────────────────────────────────────────────────────────────────────────
  const handleExecuteTask = async (task) => {
    setSelectedTask(task);
    setTaskExecutionResult(null);
    setShowExecuteModal(true);
  };

  const confirmTaskExecution = async () => {
    if (!selectedTask) return;
    
    try {
      setExecutingTask(true);
      const response = await conversationApi.executeTask(selectedTask.ticketId);
      
      if (response.success) {
        setTaskExecutionResult({
          success: true,
          message: `Task execution started successfully for: ${selectedTask.title}`,
          taskTitle: selectedTask.title
        });
      } else {
        throw new Error(response.error || 'Failed to execute task');
      }
    } catch (err) {
      setTaskExecutionResult({
        success: false,
        message: `Failed to execute task: ${err.message}`,
        taskTitle: selectedTask.title
      });
    } finally {
      setExecutingTask(false);
    }
  };

  const closeExecuteModal = () => {
    setShowExecuteModal(false);
    setSelectedTask(null);
    
    if (taskExecutionResult) {
      if (taskExecutionResult.success) setSuccess(taskExecutionResult.message);
      else setError(taskExecutionResult.message);
    }
    
    setTaskExecutionResult(null);
    setExecutingTask(false);
  };

  // ── Settings ───────────────────────────────────────────────────────────────────────────────────
  const handleSettingsChange = (newSettings) => {
    if (newSettings.dataCleared) {
      setConversations([]);
      setCurrentConversationId(null);
      setMessages([]);
      setCurrentConversationTasks([]);
      loadConversationHistory();
    } else {
      setTaskFilter(newSettings.taskFilter);
    }
  };

  const refreshConversations = async () => {
    await loadConversationHistory();
  };

  // ── UI helpers ─────────────────────────────────────────────────────────────────────────────────
  const getTaskStatusBadge = (status) => {
    const statusConfig = {
      'CREATED':    { variant: 'secondary', icon: FaClock },
      'EXECUTING':  { variant: 'primary',   icon: FaPlay },
      'PROCESSING': { variant: 'info',      icon: FaPlay },
      'COMPLETED':  { variant: 'success',   icon: FaCheckCircle },
      'FAILED':     { variant: 'danger',    icon: FaExclamationTriangle }
    };
    
    const config = statusConfig[status] || statusConfig['CREATED'];
    const IconComponent = config.icon;
    
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        <IconComponent size={10} /> {status}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      let date;
      
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = (timestamp.endsWith('Z') || timestamp.includes('+') || timestamp.includes('-'))
          ? new Date(timestamp)
          : new Date(timestamp + 'Z');
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return '';
      }
      
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch (e) {
      console.error('Error formatting timestamp:', e, timestamp);
      return '';
    }
  };

  const formatDate = (dateString) => {
    try {
      let date;
      
      if (typeof dateString === 'string') {
        date = (dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-'))
          ? new Date(dateString)
          : new Date(dateString + 'Z');
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return 'Unknown';
      
      const today     = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const dateLocal      = date.toLocaleDateString();
      const todayLocal     = today.toLocaleDateString();
      const yesterdayLocal = yesterday.toLocaleDateString();
      
      if (dateLocal === todayLocal)     return 'Today';
      if (dateLocal === yesterdayLocal) return 'Yesterday';
      
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Unknown';
    }
  };

  // ── Derived data ───────────────────────────────────────────────────────────────────────────────
  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(conversationSearch.toLowerCase())
  );

  const filteredTasks = currentConversationTasks.filter(task => {
    if (taskFilter === 'all') return true;
    return task.status.toLowerCase() === taskFilter.toLowerCase();
  });

  const getCurrentConversation = () =>
    conversations.find(conv => conv.sessionId === currentConversationId);

  // ── Render ─────────────────────────────────────────────────────────────────────────────────────
  return (
    <div className="agent-chat-page">
      <Container fluid className="agent-chat-main-container">
        <Row className="agent-chat-row">

          {/* ── Conversations Panel ─────────────────────────────────────────────────────────────── */}
          <Col 
            className={`conversations-column ${conversationsPanelExpanded ? 'expanded' : 'collapsed'}`}
            style={{
              minWidth:   conversationsPanelExpanded ? '300px' : '60px',
              maxWidth:   conversationsPanelExpanded ? '350px' : '60px',
              flex:       conversationsPanelExpanded ? '0 0 320px' : '0 0 60px',
              transition: 'all 0.3s ease'
            }}
          >
            <div className="conversations-panel">
              <div className="conversations-header">
                {conversationsPanelExpanded ? (
                  <div className="conversations-header-top">
                    <div className="conversations-title-wrapper mb-2">
                      <h6 className="conversations-title mb-0">
                        <FaComments className="me-2" />
                        Conversations ({conversations.length})
                      </h6>
                      {serverError && <small className="text-warning" style={{fontSize: '0.7rem'}}>Offline Cache</small>}
                    </div>
                    <div className="conversations-controls mb-2">
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={createNewConversation} 
                        title="New Conversation"
                      >
                        <FaPlus />
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={refreshConversations} 
                        title="Refresh" 
                        disabled={loadingConversations}
                      >
                        {loadingConversations ? <Spinner size="sm" /> : <FaSync />}
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={() => setShowSettingsModal(true)} 
                        title="Settings"
                      >
                        <FaCog />
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={toggleConversationsPanel} 
                        title="Collapse"
                      >
                        <FaCompress />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="collapsed-controls">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={toggleConversationsPanel} 
                      className="w-100 mb-2"
                    >
                      <FaExpand />
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={createNewConversation} 
                      className="w-100"
                    >
                      <FaPlus />
                    </Button>
                  </div>
                )}
              </div>
              
              {conversationsPanelExpanded && (
                <>
                  <div className="conversations-search">
                    <InputGroup size="sm">
                      <InputGroup.Text><FaSearch /></InputGroup.Text>
                      <Form.Control 
                        type="text" 
                        placeholder="Search..." 
                        value={conversationSearch} 
                        onChange={(e) => setConversationSearch(e.target.value)} 
                      />
                    </InputGroup>
                  </div>
                  
                  <div className="conversations-list">
                    {loadingConversations ? (
                      <div className="loading-state">
                        <Spinner size="sm" className="me-2" /> Loading...
                      </div>
                    ) : filteredConversations.length === 0 ? (
                      <div className="empty-state">
                        <FaComments size={32} className="mb-2 opacity-50" />
                        <p className="mb-2">No conversations yet</p>
                        <Button variant="primary" size="sm" onClick={createNewConversation}>
                          <FaPlus className="me-1" /> Start New Chat
                        </Button>
                      </div>
                    ) : (
                      <div className="conversations-scroll">
                        {filteredConversations.map((conversation) => (
                          <div
                            key={conversation.sessionId}
                            className={`conversation-item ${currentConversationId === conversation.sessionId ? 'active' : ''}`}
                            onClick={() => handleConversationSelect(conversation.sessionId)}
                          >
                            <div className="conversation-content">
                              <div className="conversation-info">
                                <h6 className="conversation-title">
                                  {conversation.title}
                                  {conversation.isTemporary && (
                                    <small className="text-muted ms-2">(temp)</small>
                                  )}
                                </h6>
                                <small className="conversation-meta">
                                  {formatDate(conversation.lastActivity)}
                                  {conversation.messageCount > 0 && (
                                    <span className="ms-2">• {conversation.messageCount} messages</span>
                                  )}
                                </small>
                              </div>
                              {conversation.source !== 'server' && (
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="conversation-delete-btn" 
                                  onClick={(e) => deleteConversation(conversation.sessionId, e)}
                                >
                                  <FaTrash size={10} />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Col>

          {/* ── Chat Column ────────────────────────────────────────────────────────────────────── */}
          <Col className="chat-column">
            <div className="chat-container">

              {/* Header */}
              <div className="chat-header">
                <div className="chat-header-content">
                  <div className="chat-header-left">
                    <img src={agentLogo} alt="Agent Enterprise" className="agent-chat-logo" />
                    <div className="chat-header-titles">
                      <h4 className="chat-title" title={getCurrentConversation()?.title || MAIN_AGENT.name}>
                        {getCurrentConversation()?.title || MAIN_AGENT.name}
                      </h4>
                      {currentConversationId && (
                        <small className="chat-session-id" title={currentConversationId}>
                          Session: {currentConversationId.substring(currentConversationId.lastIndexOf('-') + 1)}
                          {serverError && <span className="text-warning ms-2">(Offline Mode)</span>}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="chat-header-right">
                    {/* WebSocket status */}
                    <Badge 
                      bg={wsConnected ? 'success' : wsReconnecting ? 'warning' : 'danger'} 
                      className="status-flag"
                      title={wsConnected ? 'Connected' : wsReconnecting ? 'Reconnecting...' : 'Disconnected'}
                    >
                      {wsConnected ? (
                        <><FaWifi size={12} /> <span className="d-none d-sm-inline">Live</span></>
                      ) : wsReconnecting ? (
                        <><Spinner size="sm" /> <span className="d-none d-sm-inline">Reconn</span></>
                      ) : (
                        <><FaExclamationCircle size={12} /> <span className="d-none d-sm-inline">Offline</span></>
                      )}
                    </Badge>
                    
                    {!conversationsPanelExpanded && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={toggleConversationsPanel}
                        className="d-flex align-items-center"
                      >
                        <FaComments className="me-1" /> <span className="d-none d-md-inline">Conversations</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Alerts */}
              {error && (
                <div className="px-3 pt-3 pb-0">
                  <Alert variant="danger" dismissible onClose={() => setError(null)} className="m-0">
                    {error}
                  </Alert>
                </div>
              )}
              
              {success && (
                <div className="px-3 pt-3 pb-0">
                  <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="m-0">
                    {success}
                  </Alert>
                </div>
              )}
              
              {isProcessing && (
                <div className="px-3 pt-3 pb-0">
                  <Alert variant="info" className="d-flex align-items-center m-0">
                    <Spinner size="sm" className="me-2" />
                    {processingMessage || 'Processing your request...'}
                  </Alert>
                </div>
              )}

              {/* Messages area */}
              <div className="chat-messages-area">
                {!currentConversationId ? (
                  <div className="chat-empty-state">
                    <div className="mb-4" style={{ 
                      width: '100px', height: '100px', borderRadius: '50%', 
                      background: 'rgba(0, 229, 255, 0.1)', border: '2px solid var(--electric-cyan)',
                      display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center'
                    }}>
                      <FaRobot size={48} className="text-primary" />
                    </div>
                    <h3 className="mb-3">Welcome to {MAIN_AGENT.name}</h3>
                    <p className="text-muted text-center mb-4" style={{ maxWidth: '400px', lineHeight: '1.6' }}>
                      Select a conversation from the sidebar or create a new one to get started. You can use voice or text to communicate.
                    </p>
                    <Button variant="primary" onClick={createNewConversation} className="px-4 py-2 fw-bold text-dark" style={{ borderRadius: '30px' }}>
                      <FaPlus className="me-2" /> Start New Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="chat-messages-container" ref={messagesContainerRef}>
                    <div className="chat-messages-wrapper">
                      {messages.length === 0 ? (
                        <div className="chat-welcome">
                          <FaRobot size={48} className="text-muted mb-3" />
                          <h5>Start your conversation</h5>
                          <p className="text-muted">
                            Ask me to create new agents, manage tasks, or get assistance with your development workflow.
                          </p>
                        </div>
                      ) : (
                        <div className="messages-list">
                          {messages.map((message) => (
                            <div 
                              key={message.id} 
                              className={`message ${message.role} ${message.isError ? 'error' : ''}`}
                            >
                              <div className="message-avatar">
                                {message.role === 'user' ? <FaUser /> : <FaRobot />}
                              </div>
                              <div className="message-content">
                                <div className="message-header">
                                  <span className="message-sender">
                                    {message.role === 'user' ? 'You' : MAIN_AGENT.name}
                                  </span>
                                  <span className="message-time">
                                    {formatTimestamp(message.timestamp)}
                                  </span>
                                </div>
                                <div className="message-text">
                                  {message.content}
                                </div>
                                {(message.intent && message.intent !== 'GENERAL_QUESTION') && (
                                  <div className="message-meta mt-2">
                                    <Badge bg="info" size="sm">
                                      Intent: {message.intent}
                                    </Badge>
                                    {message.ticketId && (
                                      <Badge bg="success" size="sm" className="ms-2">
                                        Task: {message.ticketId.substring(0, 8)}...
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {loading && (
                            <div className="message assistant">
                              <div className="message-avatar bg-dark border border-info text-info"><FaRobot /></div>
                              <div className="message-content">
                                <div className="typing-indicator bg-dark rounded px-3 py-2 border border-secondary" style={{display: 'inline-block'}}>
                                  <Spinner size="sm" className="me-2 text-info" />
                                  <span className="text-muted">{processingMessage || `${MAIN_AGENT.name} is thinking...`}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Input area ─────────────────────────────────────────────────────────────────── */}
              {currentConversationId && (
                <div className="chat-input-area">
                  <div className="chat-input-wrapper">

                    {/* Voice error banner */}
                    {(voiceError || recorderError) && (
                      <div className="voice-error-banner w-100 d-flex justify-content-between align-items-center px-3 pt-2 pb-1">
                        <small className="text-danger m-0 fw-bold">
                          <FaMicrophone className="me-1" /> {voiceError || recorderError}
                        </small>
                        <button
                          className="btn-close btn-close-white"
                          style={{ fontSize: '0.6rem' }}
                          onClick={() => setVoiceError(null)}
                          aria-label="Dismiss voice error"
                        ></button>
                      </div>
                    )}

                    <Form onSubmit={handleSendMessage} className="d-flex gap-2 align-items-center flex-grow-1 w-100 px-1 py-1">

                      {/* VoiceRecordButton - smartly expands when recording */}
                      <VoiceRecordButton
                          isRecording={isRecording}
                          isPaused={isPaused}
                          isTranscribing={isTranscribing}
                          transcribeProgress={transcribeProgress}   
                          isSupported={isVoiceSupported}
                          recordingDuration={recordingDuration}
                          formattedDuration={formattedDuration}
                          audioLevel={audioLevel}
                          error={voiceError || recorderError}
                          onStart={startRecording}
                          onStop={() => stopAndTranscribe(transcribeAudio)}
                          onPause={pauseRecording}
                          onResume={resumeRecording}
                          onCancel={cancelRecording}
                          disabled={loading || isProcessing}
                        />

                      {/* Text input - hidden when recording or transcribing */}
                      {(!isRecording && !isTranscribing) && (
                        <>
                          <Form.Control
                            ref={messageInputRef}
                            type="text"
                            placeholder={`Send a message to ${MAIN_AGENT.name}...`}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onFocus={handleMessageInputFocus}
                            disabled={loading || isProcessing}
                            className="message-input flex-grow-1"
                            autoComplete="off"
                          />

                          <Button 
                            type="submit" 
                            variant="primary" 
                            disabled={loading || isProcessing || !newMessage.trim()} 
                            className="send-button ms-1"
                          >
                            {loading || isProcessing ? <Spinner size="sm" /> : <FaPaperPlane style={{marginLeft: '-2px'}} />}
                          </Button>
                        </>
                      )}
                    </Form>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* ── Tasks Panel ────────────────────────────────────────────────────────────────────── */}
          <Col 
            className="tasks-column" 
            style={{ minWidth: '280px', maxWidth: '320px', flex: '0 0 300px' }}
          >
            <div className="tasks-panel">
              <div className="tasks-header">
                <div className="tasks-header-content">
                  <h6 className="tasks-title">
                    <FaTasks style={{ marginRight: '8px' }} /> Tasks ({currentConversationTasks.length})
                  </h6>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={() => loadTasksForConversation(currentConversationId)} 
                    disabled={loadingTasks}
                  >
                    {loadingTasks ? <Spinner size="sm" /> : <FaSync />}
                  </Button>
                </div>
                <Form.Select 
                  size="sm" 
                  value={taskFilter} 
                  onChange={(e) => setTaskFilter(e.target.value)} 
                  className="mt-2"
                >
                  <option value="all">All Tasks</option>
                  <option value="created">Created</option>
                  <option value="executing">Executing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </div>
              
              <div className="tasks-content">
                {!currentConversationId ? (
                  <div className="tasks-empty-state">
                    <FaTasks size={24} className="mb-2 opacity-50" />
                    <p>Select a conversation to view tasks</p>
                  </div>
                ) : loadingTasks ? (
                  <div className="tasks-loading-state">
                    <Spinner size="sm" className="me-2" /> Loading tasks...
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="tasks-empty-state">
                    <FaTasks size={24} className="mb-2 opacity-50" />
                    <p>No tasks found</p>
                  </div>
                ) : (
                  <div className="tasks-list">
                    {filteredTasks.map((task) => (
                      <div key={task.ticketId} className="task-item">
                        <div className="task-header">
                          <h6 className="task-title">{task.title}</h6>
                          <div className="task-status">
                            {getTaskStatusBadge(task.status)}
                          </div>
                        </div>
                        <p className="task-description">
                          {task.description || task.details?.description || 'No description available'}
                        </p>
                        <div className="task-meta">
                          <small>
                            <strong>Created:</strong> {new Date(task.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                        <div className="task-actions">
                          {(task.status === 'CREATED' || task.status === 'FAILED') && (
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => handleExecuteTask(task)}
                            >
                              <FaPlay size={8} className="me-1" /> Execute
                            </Button>
                          )}
                          <Button variant="outline-secondary" size="sm">
                            <FaHistory size={8} className="me-1" /> Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* ── Execute Task Modal ─────────────────────────────────────────────────────────────────── */}
      <CustomModal 
        show={showExecuteModal} 
        onHide={closeExecuteModal} 
        title={taskExecutionResult ? 'Task Execution Result' : 'Confirm Task Execution'} 
        size="lg" 
        footer={
          taskExecutionResult ? (
            <Button variant="primary" onClick={closeExecuteModal}>Close</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={closeExecuteModal}>Cancel</Button>
              <Button 
                variant="primary" 
                onClick={confirmTaskExecution} 
                disabled={executingTask}
              >
                {executingTask ? (
                  <><Spinner size="sm" className="me-2" /> Executing...</>
                ) : (
                  <><FaPlay className="me-2" /> Execute Task</>
                )}
              </Button>
            </>
          )
        }
      >
        {taskExecutionResult ? (
          <div className={`alert ${taskExecutionResult.success ? 'alert-success' : 'alert-danger'}`}>
            <div className="d-flex align-items-center">
              {taskExecutionResult.success ? (
                <FaCheckCircle className="me-2" size={24} />
              ) : (
                <FaExclamationTriangle className="me-2" size={24} />
              )}
              <div>
                <h6 className="mb-1">{taskExecutionResult.success ? 'Success!' : 'Error'}</h6>
                <p className="mb-0">{taskExecutionResult.message}</p>
              </div>
            </div>
          </div>
        ) : (
          selectedTask && (
            <>
              <p>Are you sure you want to execute this task?</p>
              <div className="task-summary p-3 bg-light rounded text-dark">
                <h6 className="text-dark">{selectedTask.title}</h6>
                <p className="mb-2 text-dark">{selectedTask.description}</p>
              </div>
            </>
          )
        )}
      </CustomModal>

      {/* ── Settings Modal ─────────────────────────────────────────────────────────────────────── */}
      <ConversationSettingsModal 
        show={showSettingsModal} 
        onHide={() => setShowSettingsModal(false)} 
        onSettingsChange={handleSettingsChange} 
      />
    </div>
  );
};

export default AgentChat;